import { computed, action, observable } from 'mobx';
import { ViewController } from 'elc-service';
import { root } from 'elc-universal-utils';
import { Breakpoints } from 'elc-base-theme';
import * as VtoConstants from '../constants/Vto';
import { query, serviceQueries } from 'elc-service-bus';
import {
    IVtoConfig,
    IProduct,
    IVtoEventListenerResult,
    IVtoFoundationShadeLabels,
    IVtoFoundationPerfectMatches,
    IProductSku
} from '../interfaces/IVto';
import { IVtoSkinConcernScores } from '../interfaces/IVtoSkinCare';
import { IFeature } from '../constants/Features';

export interface IVtoApiMethods {
    init({}): void;
    open(liveCam: boolean, image: object | null, detectSkin: boolean): void;
    closeEngine(): void;
    close(): void;
    applyMakeupBySku(skuId: string): void;
    addEventListener(
        event: string,
        callback: (result: IVtoEventListenerResult | IVtoSkinConcernScores) => void
    ): number;
    removeEventListener(listenerId: number): void;
    openSkincare(skincareMode: string): void;
    getSkincareRecommendationList({}): void;
}

export interface IVtoWindow extends Window {
    ymkAsyncInit(): void;
    YMK: IVtoApiMethods;
}

declare let window: IVtoWindow;

export class VtoBaseController extends ViewController {
    @observable public product: IProduct;
    public isVtoFoundationEnabled: boolean;
    public isVtoMakeupEnabled: boolean;
    public vtoFoundationPerfectMatchText: IVtoFoundationShadeLabels;
    public handleShadeSelect: (name: string, productId: string) => void;
    public bodyElementClass: DOMTokenList = document.body.classList;
    public eventListenerIdentifiers: number[] = [];
    public iframeWidth: number;
    public iframeHeight: number;
    public vtoConfig: IVtoConfig;
    public isVtoDisclosed: boolean = false;
    public isVtoFoundationMultiIds: boolean;
    public productIdList: string[];

    @computed
    get vtoFeature() {
        return this.vtoConfig && this.vtoConfig.vtoFeature;
    }

    @computed
    get vtoFoundationFeature() {
        return this.vtoFeature && this.vtoFeature.foundation;
    }

    @computed
    public get getVtoApiConfig() {
        if (!this.vtoConfig || !this.vtoConfig.features) {
            return;
        }

        if (this.isVtoFoundationEnabled) {
            return this.vtoConfig.features.vtoFoundation;
        }

        return this.vtoConfig.features.vtoMakeup;
    }

    public loadVto = (apiUrl: string, apiKey: string) => {
        if (!!document.querySelector(`script[src="${apiUrl}${apiKey}"]`)) {
            return;
        }
        const script = document.getElementsByTagName('script')[0];
        const vtoScript = document.createElement('script');
        vtoScript.type = 'text/javascript';
        vtoScript.async = true;
        vtoScript.src = `${apiUrl}${apiKey}`;
        if (script !== undefined && script.parentNode !== null) {
            script.parentNode.insertBefore(vtoScript, script);
        }
    };

    public getIframeDimension = () => {
        // Get image container width
        const imageContainer: HTMLElement | null =
            window.innerWidth >= Breakpoints.desktop
                ? document.querySelector('.js-left-sticky-column') ||
                  document.querySelector('.js-left-column-section')
                : document.querySelector('.js-product-full');
        const imageContainerWidth = (imageContainer as HTMLElement).offsetWidth;

        // Reduce extra padding for larger screens
        const imageContainerModifiedWidth =
            window.innerWidth >= Breakpoints.desktop
                ? imageContainerWidth - VtoConstants.IFRAME_EXTRA_PADDING
                : imageContainerWidth;

        let iframeWidth = imageContainerModifiedWidth;

        // Set max width if the container exceeds max width
        if (imageContainerModifiedWidth > VtoConstants.IFRAME_MAX_WIDTH) {
            iframeWidth = VtoConstants.IFRAME_MAX_WIDTH;
        }

        // Set min width if the container width is lesser than the min width
        if (imageContainerModifiedWidth < VtoConstants.IFRAME_MIN_WIDTH) {
            iframeWidth = VtoConstants.IFRAME_MIN_WIDTH;
        }
        // Calculate height using VTO ratio
        const iframeHeight = iframeWidth * VtoConstants.IFRAME_RATIO;

        return {
            iframeWidth,
            iframeHeight
        };
    };

    public handleVtoResize = (reset: boolean) => {
        const hasLipVto = this.vtoFoundationFeature && this.vtoFoundationFeature.hasLipVto;
        if (hasLipVto) {
            const iframe = document.getElementById('YMK-module-iframe');
            const maxHeight = window.innerHeight;
            const compareOverlayOffset =
                this.vtoFoundationFeature && this.vtoFoundationFeature.compareOverlayOffset;

            if (iframe && compareOverlayOffset) {
                iframe.style.height = reset
                    ? `${screen.height}px`
                    : `${maxHeight - compareOverlayOffset}px`;
            }
        }
    };

    public getVtoParams = () => {
        const compareOverlayOffset =
            this.vtoFoundationFeature && this.vtoFoundationFeature.compareOverlayOffset;
        const bottomPanelOffsetY = compareOverlayOffset || VtoConstants.COMPARE_BUTTON_HEIGHT;
        const isDesktop = window.innerWidth >= Breakpoints.desktop;
        const { iframeWidth, iframeHeight } = this.getIframeDimension();
        const { language } = <IFeature>this.getVtoApiConfig;
        this.iframeWidth = iframeWidth;
        this.iframeHeight = iframeHeight;

        if (this.isVtoFoundationEnabled) {
            return {
                autoOpen: false,
                showCloseBtnOnLauncher: true,
                bottomPanelOffsetY: bottomPanelOffsetY,
                ...(this.isVtoFoundationMultiIds && {
                    productIdList: this.productIdList
                }),
                ...(language && { language })
            };
        }

        return {
            autoOpen: true,
            skinSmoothStrength: VtoConstants.SKIN_SMOOTH_STRENGTH,
            showCloseBtnOnLauncher: !this.isVtoDisclosed,
            width: isDesktop ? iframeWidth : '',
            height: isDesktop ? iframeHeight : '',
            ...(language && { language })
        };
    };

    public filterPerfectMatches = (perfectMatchResults: IVtoFoundationPerfectMatches) => {
        const vtoFoundationShadeValues = Object.values(VtoConstants.FoundationShadeTypes);

        return Object.entries(perfectMatchResults).reduce(
            (perfectMatchAccumulator, [label, skuId]) => {
                // VTO sends us the best shade in the name of "guid"
                const shadeLabel =
                    label === VtoConstants.VTO_GUID
                        ? VtoConstants.FoundationShadeTypes.BEST
                        : (label as VtoConstants.FoundationShadeTypes);
                if (vtoFoundationShadeValues.indexOf(shadeLabel) > -1) {
                    perfectMatchAccumulator[skuId] = this.vtoFoundationPerfectMatchText[shadeLabel];
                }

                return perfectMatchAccumulator;
            },
            {} as IVtoFoundationPerfectMatches
        );
    };

    public updateShadeEntity = (perfectMatches: IVtoFoundationPerfectMatches) => {
        const perfectMatchSkuIds = Object.keys(perfectMatches);
        this.product.shades.forEach((shade) => {
            if (shade.parentSku && perfectMatchSkuIds.indexOf(shade.parentSku.skuId) > -1) {
                const perfectShadeLabel = perfectMatches[shade.parentSku.skuId];
                shade.changeToPerfect && shade.changeToPerfect(perfectShadeLabel);
            } else {
                shade.resetPerfect && shade.resetPerfect();
                shade.showNameInsideShadeCircle && shade.showNameInsideShadeCircle();
            }
        });
    };

    public selectPerfectSku = async (result?: IVtoFoundationPerfectMatches) => {
        // VTO sends us the best shade in the name of "guid"
        const skuId = result && result[VtoConstants.VTO_GUID];
        if (skuId) {
            const skuData = <IProductSku>await query(serviceQueries.GET_SKUS, [skuId]);
            const { shades } = skuData[skuId];
            if (this.handleShadeSelect) {
                this.handleShadeSelect(shades[0].name, this.product.productId);
            }
        }
    };

    public resetShadeProps = () => {
        if (this.isVtoMakeupEnabled) {
            return;
        }

        this.product.shades.forEach((shade) => shade.resetPerfect && shade.resetPerfect());
    };

    public removeVtoEventListeners = () => {
        if (!this.eventListenerIdentifiers.length) {
            return;
        }

        this.eventListenerIdentifiers.forEach((eventListenerIdentifier) =>
            window.YMK.removeEventListener(eventListenerIdentifier)
        );
        this.eventListenerIdentifiers = [];
    };

    public toggleBodyClasses = (hasBodyClasses: boolean) => {
        const isMobile = window.innerWidth < Breakpoints.desktop;
        const bodyClasses = [VtoConstants.NO_SCROLL_CLASS, VtoConstants.VTO_ACTIVE_CLASS];
        isMobile && hasBodyClasses
            ? this.bodyElementClass.add(...bodyClasses)
            : this.bodyElementClass.remove(...bodyClasses);
    };

    public checkVtoDisclose = () => {
        const vtoDiscloseParam = this.getQueryParam(VtoConstants.VTO_DISCLOSE_PARAM);
        this.isVtoDisclosed = vtoDiscloseParam !== null;
    };

    public getQueryParam = (searchKey: string) => {
        const search = (<Location>root.location).search;
        const urlParams = new URLSearchParams(search);

        return urlParams.get(searchKey);
    };

    @action
    public updateProduct = (product: IProduct) => {
        this.product = product;
    };
}
